import {
  Component,
  OnInit,
  inject,
  runInInjectionContext,
  effect,
  Injector,
  ViewChild,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { PAGE } from './routes';
import { IonModal, Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { initializeApp, FirebaseOptions } from 'firebase/app';
import { OperatorService } from './core/services/operator/operator.service';
import { EventName } from './core/services/analytics/event-names';
import { AuthService } from './core/services/auth/auth.service';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { Theme, ThemeService } from './core/services/theme/theme.service';
import { InitService } from './core/services/init/init.service';
import { BootupService } from './core/services/bootup/bootup.service';
import { ActivityFeedService } from './core/services/activity-feed/activity-feed.service';
import { TranslocoService } from '@jsverse/transloco';
import { JobService } from './core/services/job/job.service';
import { Job } from './core/services/job/job';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild(IonModal) modal!: IonModal;

  private auth = inject(AuthService);
  private router = inject(Router);
  private platform = inject(Platform);
  private analyticsService = inject(AnalyticsService);
  private injector = inject(Injector);
  private initService = inject(InitService);
  private bootupService = inject(BootupService);
  private activityFeedService = inject(ActivityFeedService);
  private operatorService = inject(OperatorService);
  private translocoService = inject(TranslocoService);
  private jobService = inject(JobService);

  Theme = Theme;
  themeService = inject(ThemeService);
  operatorTabOptions?: { label: string; id: string }[];
  recentMessages = signal<string[]>([]);

  private async initialise() {
    if (!OperatorService.IsDemo) {
      initializeApp(environment.firebase as FirebaseOptions);

      await this.analyticsService.initialise();
    }

    const job = this.jobService.create(async () => {
      await this.bootupService.initialise();
    });
    job.showNoInternetRetry(Job.InfiniteRetry);
    job.addErrorHandler().showFatalError().reloadWindow();
    await job.runNoThrow();

    this.analyticsService.log(EventName.FirebaseInitialised);

    // Move initService.initialise() here, after Firebase is ready
    this.initService.initialise();

    runInInjectionContext(this.injector, () => {
      effect(async () => {
        const initialised = this.initService.initialised();
        if (initialised) {
          try {
            this.activityFeedService.initialize();
          } catch (error) {
            console.error(error);
          }
        }
      });
    });
  }

  async ngOnInit(): Promise<void> {
    await this.initialise();
    this.updateTranslations();
  }

  private updateTranslations(): void {
    this.translocoService
      .selectTranslate([
        'games.solitaire-name',
        'games.blocks-name',
        'games.blackjack-name',
        'tabs.play',
        'tabs.results',
        'tabs.leaderboard',
        'tabs.rules',
      ])
      .subscribe((translations) => {
        this.operatorTabOptions = [
          { label: translations[3], id: `${PAGE.GAME}/${this.operatorService.gameId}` },
          { label: translations[4], id: PAGE.RESULTS },
          { label: translations[5], id: PAGE.CHOOSE_LEADERBOARDS },
          { label: translations[6], id: `${PAGE.SELECT_RULES}/${this.operatorService.gameId}` },
        ];
      });
  }

  onCloseClicked() {
    this.auth
      .signOut()
      .catch((error) => {
        this.analyticsService.handleError(error, 'App back to operator sign out');
      })
      .finally(() => {
        this.operatorService.redirectToOperator(); // always redirect
      });
  }

  get backgroundImage(): string {
    if (this.themeService.hasBackgroundImg()) {
      return `url('/assets/images/${this.themeService.currentTheme()}/background.jpg')`;
    }
    return `none`;
  }

  onThemeClicked() {
    this.modal.present();
  }

  onThemeCloseClick() {
    this.modal.dismiss();
  }

  async canDismiss(data?: unknown, role?: string) {
    return role !== 'gesture';
  }

  isMobile(): boolean {
    return this.platform.is('mobile');
  }

  onHeaderCloseClicked() {
    this.onCloseClicked();
  }

  onHeaderThemeClicked() {
    this.onThemeClicked();
  }

  onHeaderTabSelected(tabId: string) {
    this.router.navigate([tabId]);
  }
}
